import type { FriendlyKeyValueUrl, PageEnvelope } from "@skv/core";
import { passZippedFile } from "@skv/util";
import { Machine, assign } from "xstate";

interface SearchFormStateSchema {
  states: {
    initialSuggestions: {
      states: {
        idle: Record<string, unknown>;
        loading: Record<string, unknown>;
        loaded: Record<string, unknown>;
        failed: Record<string, unknown>;
      };
    };
    searchLocationsFullList: {
      states: {
        idle: Record<string, unknown>;
        loading: Record<string, unknown>;
        loaded: Record<string, unknown>;
        failed: Record<string, unknown>;
      };
    };
    relatedSuggestions: {
      states: {
        idle: Record<string, unknown>;
        loading: Record<string, unknown>;
        loaded: Record<string, unknown>;
        failed: Record<string, unknown>;
      };
    };
  };
}

export type SearchFormEventBase =
  | {
      type: "SELECT";
      value: string[];
    }
  | {
      type: "CLEAR_ALL_INPUTS";
    }
  | {
      type: "KEYWORD_INPUT_CLEAR";
    }
  | {
      type: "KEYWORD_INPUT_CHANGE";
      value: string;
    }
  | {
      type: "KEYWORD_INPUT_SELECT";
      value: FriendlyKeyValueUrl;
    }
  | {
      type: "LOCATION_INPUT_CLEAR";
    }
  | {
      type: "LOCATION_INPUT_CHANGE";
      value: string;
    }
  | {
      type: "LOCATION_INPUT_SELECT";
      value: string;
    }
  | {
      type: "FETCH_START_INITIAL_SUGGESTIONS";
    }
  | {
      type: "FETCH_START_LOCATIONS";
      value: string;
    }
  | {
      type: "";
    };

export type SearchFormEvent =
  | SearchFormEventBase
  | {
      type: "FETCH_SEARCH_LOCATIONS_FULL_LIST";
    };

interface SearchFormContext {
  keyword: string;
  where: string;
  category: number[];
  didKeywordAutoComplete: boolean;
  didWhereAutoComplete: boolean;
  initialSuggestions: string[];
  searchLocationsFullList: string[];
  relatedSuggestions: string[];

  cache: {
    [key: string]: string[];
  };
}

let INITIAL_SUGGESTIONS_CACHE: string[] | null = null;
let SEARCH_LOCATIONS_FULL_LIST_CACHE: string[] | null = null;
const WHERE_STORE_NAME = "searchFormWhere";

/* Guards */
const locationSearchValid = (ctx, event) => {
  if ("value" in event) {
    return event.value.length > 2;
  }

  return false;
};

const idleLocationHasValidValue = (ctx, event) => {
  return ctx.where.length > 2;
};

const CLOUDFRONT_URL =
  process.env.TARGET_ENV === "nz"
    ? "https://cdn.seekvolunteer.co.nz"
    : "https://cdn.volunteer.com.au";

export const searchFormMachine = Machine<
  SearchFormContext,
  SearchFormStateSchema,
  SearchFormEvent
>({
  id: "typeahead",
  type: "parallel",
  context: {
    keyword: "",
    where: "",
    category: [],
    didKeywordAutoComplete: false,
    didWhereAutoComplete: false,
    initialSuggestions: [],
    searchLocationsFullList: [],
    relatedSuggestions: [],
    cache: {},
  },
  states: {
    initialSuggestions: {
      initial: INITIAL_SUGGESTIONS_CACHE ? "loaded" : "loading",
      states: {
        idle: {
          on: {
            FETCH_START_INITIAL_SUGGESTIONS: "loading",
          },
        },
        loading: {
          invoke: {
            id: "fetch_initial_suggestions",
            src: (ctx) => {
              if (INITIAL_SUGGESTIONS_CACHE) {
                return Promise.resolve().then(() => INITIAL_SUGGESTIONS_CACHE);
              }

              return fetch("/api/majorcities")
                .then<PageEnvelope<{ locations: string[] }>>((res) =>
                  res.json()
                )
                .then(({ data }) => {
                  INITIAL_SUGGESTIONS_CACHE = data.locations;
                  return data.locations;
                });
            },
            onDone: {
              target: "loaded",
              actions: assign({
                initialSuggestions: (ctx, event) => event.data,
              }),
            },
            onError: "failed",
          },
        },
        loaded: {},
        failed: {},
      },
    },
    searchLocationsFullList: {
      initial: SEARCH_LOCATIONS_FULL_LIST_CACHE ? "loaded" : "loading",
      states: {
        idle: {
          on: {
            FETCH_SEARCH_LOCATIONS_FULL_LIST: "loading",
          },
        },
        loading: {
          invoke: {
            id: "fetch_search_locations_full_list",
            src: (ctx, event) => {
              if (SEARCH_LOCATIONS_FULL_LIST_CACHE) {
                return Promise.resolve().then(
                  () => SEARCH_LOCATIONS_FULL_LIST_CACHE
                );
              }

              return fetch(`${CLOUDFRONT_URL}/data/all-locations.gz`)
                .then(async (res) => {
                  if (!res.ok) throw new Error("Network response was not ok");

                  const searchResults: string[] = await passZippedFile(res);

                  SEARCH_LOCATIONS_FULL_LIST_CACHE = searchResults;
                  return searchResults;
                })
                .catch(() => {
                  return fetch("/api/locations/all")
                    .then((res) => {
                      if (!res.ok)
                        throw new Error("Network response was not ok");
                      return res.json();
                    })
                    .then(({ data }) => {
                      SEARCH_LOCATIONS_FULL_LIST_CACHE = data.locations;
                      return data.locations;
                    });
                });
            },
            onDone: {
              target: "loaded",
              actions: assign({
                searchLocationsFullList: (ctx, event) => event.data,
              }),
            },
            onError: "failed",
          },
        },
        loaded: {},
        failed: {},
      },
    },
    relatedSuggestions: {
      initial: "idle",
      states: {
        idle: {
          always: [
            {
              target: "loading",
              cond: idleLocationHasValidValue,
            },
          ],
        },
        loading: {
          invoke: {
            id: "fetch_location_suggestions",
            src: (ctx) => {
              if (ctx.cache[ctx.where]) {
                return Promise.resolve().then(() => ctx.cache[ctx.where]);
              }

              const jsonCache =
                sessionStorage?.getItem(WHERE_STORE_NAME) || "{}";
              const whereCache = JSON.parse(jsonCache) as Record<
                string,
                string[]
              >;

              if (whereCache.hasOwnProperty(ctx.where)) {
                return Promise.resolve().then(() => whereCache[ctx.where]);
              }

              if (SEARCH_LOCATIONS_FULL_LIST_CACHE?.length > 0) {
                return Promise.resolve(SEARCH_LOCATIONS_FULL_LIST_CACHE);
              }

              return fetch(`/api/locations?q=${ctx.where}`)
                .then<PageEnvelope<{ locations: string[] }>>((res) =>
                  res.json()
                )
                .then(({ data }) => {
                  try {
                    whereCache[ctx.where] = [...data.locations];
                    sessionStorage?.setItem(
                      WHERE_STORE_NAME,
                      JSON.stringify(whereCache)
                    );
                  } catch (e) {
                    console.error("Error storing search form where: ", e);
                  }

                  return data.locations;
                });
            },
            onDone: {
              target: "loaded",
              actions: assign({
                cache: (ctx, event) =>
                  Object.assign({}, ctx.cache, {
                    [ctx.where]: event.data,
                  }),
                relatedSuggestions: (ctx, event) => event.data,
              }),
            },
            onError: "failed",
          },
        },
        loaded: {},
        failed: {},
      },
    },
  },
  on: {
    CLEAR_ALL_INPUTS: {
      actions: assign({
        keyword: (ctx) => "",
        category: (ctx) => [],
        where: (ctx) => "",
        didKeywordAutoComplete: (ctx) => false,
        didWhereAutoComplete: (ctx) => false,
        cache: (ctx) => {
          const tempCache = Object.assign({}, ctx.cache);
          delete tempCache[ctx.keyword];
          delete tempCache[ctx.where];
          return tempCache;
        },
      }),
    },
    KEYWORD_INPUT_CLEAR: {
      actions: assign({
        keyword: (ctx) => "",
        category: (ctx) => [],
        didKeywordAutoComplete: (ctx) => false,
        cache: (ctx) => {
          const tempCache = Object.assign({}, ctx.cache);
          delete tempCache[ctx.keyword];
          return tempCache;
        },
      }),
    },
    KEYWORD_INPUT_CHANGE: {
      actions: assign({
        keyword: (ctx, e) => e.value,
        category: (ctx) => [],
        didKeywordAutoComplete: (ctx) => false,
      }),
    },
    KEYWORD_INPUT_SELECT: {
      actions: assign({
        keyword: (ctx) => "",
        category: (ctx, e) => [e.value.id],
        didKeywordAutoComplete: (ctx) => true,
      }),
    },
    LOCATION_INPUT_CLEAR: {
      actions: assign({
        where: (ctx) => "",
        didWhereAutoComplete: (ctx) => false,
      }),
    },
    LOCATION_INPUT_CHANGE: {
      actions: assign({
        where: (ctx, e) => e.value,
        didWhereAutoComplete: (ctx) => false,
      }),
    },
    LOCATION_INPUT_SELECT: [
      {
        target: "relatedSuggestions.loading",
        cond: locationSearchValid,
        actions: assign({
          where: (ctx, e) => e.value || ctx.where || "",
          didWhereAutoComplete: (ctx) => true,
        }),
      },
    ],
    FETCH_START_LOCATIONS: {
      target: "relatedSuggestions.loading",
      cond: locationSearchValid,
      actions: assign({
        where: (ctx, e) => e.value || ctx.where || "",
      }),
    },
  },
});
